import React, { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';
import { mergeRefs, useAnchorPosition } from '../../lib/useAnchorPosition';

type PopoverContextValue = {
  open: boolean;
  onOpenChange: ((open: boolean) => void) | undefined;
  triggerEl: HTMLElement | null;
  setTriggerEl: (el: HTMLElement | null) => void;
};

const PopoverContext = createContext<PopoverContextValue>({
  open: false,
  onOpenChange: undefined,
  triggerEl: null,
  setTriggerEl: () => {},
});

export function Popover({
  open = false,
  onOpenChange,
  children,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}) {
  const [triggerEl, setTriggerEl] = useState<HTMLElement | null>(null);

  return (
    <PopoverContext.Provider value={{ open, onOpenChange, triggerEl, setTriggerEl }}>
      {children}
    </PopoverContext.Provider>
  );
}

export function PopoverTrigger({
  children,
  asChild,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }) {
  const { open, onOpenChange, setTriggerEl } = useContext(PopoverContext);

  const toggle = () => onOpenChange?.(!open);

  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<Record<string, unknown>>;
    return React.cloneElement(child, {
      ...props,
      ref: mergeRefs(
        setTriggerEl,
        (child as { ref?: React.Ref<HTMLElement> }).ref,
      ),
      onClick: (event: React.MouseEvent) => {
        (child.props as { onClick?: (e: React.MouseEvent) => void }).onClick?.(event);
        toggle();
      },
      'aria-expanded': open,
    });
  }

  return (
    <button
      type="button"
      ref={setTriggerEl}
      aria-expanded={open}
      onClick={toggle}
      {...props}
    >
      {children}
    </button>
  );
}

export function PopoverContent({
  children,
  className,
  align = 'start',
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { align?: 'start' | 'center' | 'end' }) {
  const { open, onOpenChange, triggerEl } = useContext(PopoverContext);
  const pos = useAnchorPosition(triggerEl, open);

  if (!open || typeof document === 'undefined') return null;

  let left = pos.left;
  if (align === 'center' && triggerEl) {
    const contentWidth = triggerEl.getBoundingClientRect().width;
    left = pos.left + contentWidth / 2;
  } else if (align === 'end' && triggerEl) {
    left = pos.left + triggerEl.getBoundingClientRect().width;
  }

  return createPortal(
    <>
      <div className="fixed inset-0 z-[400]" onClick={() => onOpenChange?.(false)} />
      <div
        className={cn(
          'fixed z-[401] rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-3)]',
          align === 'center' && '-translate-x-1/2',
          align === 'end' && '-translate-x-full',
          className,
        )}
        style={{
          top: pos.top,
          left,
          minWidth: pos.width > 0 ? pos.width : undefined,
        }}
        {...props}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
